// Phase T2.0A Prompt 2 — Event header (member-facing).
//
// Renders the event title as the visual priority + a small row of pills:
//   • Composition (Open / Men's / Women's / Mixed)
//   • Event type (Singles / Doubles)
//   • Status (Registration open, Registration closed, In progress, etc.)
//
// A gold hairline sits above the header — same brand accent used across the
// tournament + round robin cards.

import React from 'react';

const COMP_LABEL = {
  open: 'Open',
  mens: "Men's",
  womens: "Women's",
  mixed: 'Mixed',
};

const STATUS_LABEL = {
  draft: 'Draft',
  registration_open: 'Registration open',
  registration_closed: 'Registration closed',
  bracket_generated: 'Bracket generated',
  schedule_generated: 'Schedule generated',
  in_progress: 'In progress',
  completed: 'Completed',
  archived: 'Archived',
  cancelled: 'Cancelled',
};

function statusPillClass(status) {
  switch (status) {
    case 'registration_open':    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    case 'registration_closed':  return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'bracket_generated':
    case 'schedule_generated':   return 'bg-sky-50 text-sky-800 border-sky-200';
    case 'in_progress':          return 'bg-indigo-50 text-indigo-800 border-indigo-200';
    case 'completed':            return 'bg-stone-100 text-stone-800 border-stone-300';
    case 'archived':
    case 'cancelled':            return 'bg-zinc-100 text-zinc-600 border-zinc-300';
    default:                     return 'bg-[#f3f1ec] text-[#6a766e] border-[#e3dcd1]';
  }
}

export default function EventHeader({
  name,
  composition = 'open',
  eventType,          // 'singles' | 'doubles'
  status,
  actions,            // optional right-side ReactNode (Manage button, etc.)
}) {
  return (
    <div
      className="relative overflow-hidden border border-[#e3dcd1] rounded-2xl bg-white"
      data-testid="event-header"
    >
      <div
        className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#c9a93a] via-[#013523] to-[#015e3f]"
        aria-hidden
      />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                data-testid="event-header-composition"
                className="text-[10px] px-2 py-0.5 rounded-full bg-[#f3f1ec] text-[#013523] border border-[#e3dcd1] uppercase tracking-wider font-medium"
              >
                {COMP_LABEL[composition] || composition} · {eventType === 'singles' ? 'Singles' : 'Doubles'}
              </span>
              <span
                data-testid="event-header-status"
                className={`text-[10px] px-2 py-0.5 rounded-full border ${statusPillClass(status)}`}
              >
                {STATUS_LABEL[status] || status}
              </span>
            </div>
            <h1
              data-testid="event-header-title"
              className="mt-2 font-serif text-2xl sm:text-3xl text-[#013523] tracking-tight break-words"
            >
              {name}
            </h1>
          </div>
          {actions && <div className="flex gap-2 flex-wrap shrink-0">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
